import React, { useState, useContext, useEffect, useRef } from 'react';
import { Drawer, RadioGroup, Radio, FormControlLabel, Checkbox, MenuList, MenuItem } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import _ from 'lodash';
import { isMobileOnly, isTablet } from 'react-device-detect';

import AppContext from '../../../App.context';
import './UserDrawer.scss';
import Icon from '../../Icon/Icon';
import DropDown from '../../DropDown/DropDown';
import PersonalData from '../../Account/PersonalData/PersonalData';
import AccountHistory from '../../Account/AccountHistory/AccountHistory';
import AccountLimits from '../../Account/AccountLimits/AccountLimits';
import DepositFunds from '../../Account/DepositFunds/DepositFunds';
import WithdrawFunds from '../../Account/WithdrawFunds/WithdrawFunds';
import AppModeContext from 'src/containers/AppMode/AppMode.context';

function UserDrawer() {
  const [ open, setOpen ] = useState(false);
  const [ menu, setMenu ] = useState('settings');
  const history = useHistory();
  const { pathname } = useLocation();
  const { t } = useTranslation(['common']);
  const { isPortrait } = useContext(AppContext);
  const { RETAIL_MODE } = useContext(AppModeContext);
  const query = new URLSearchParams(location.search);

  // REDUX
  const { settings, openMenu } = useSelector(state => state.content);
  const { customer, localWalletSelected } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const drawer = useRef(null);

  const handleSettings = (e) => {
    dispatch.content.updateSettings({
      [e.target.name]: e.target.checked
    });
  };

  const onUserMenuSelect = option => event => {
    event.preventDefault();
    if ((isMobileOnly || (isTablet && isPortrait)) && !_.isEqual(option, 'logout')) {
      setMenu(option);
    } else {
      setOpen(false);

      switch (option) {
        case 'logout':
          dispatch.auth.logout();
          if (!RETAIL_MODE){
            history.push(`${ pathname }#logout`);
          }
          break;

        case 'personalData':
          history.push('/account');
          break;

        case 'bettingHistory':
          history.push('/account/betting-history');
          break;

        case 'accountStatement':
          history.push('/account/account-statement');
          break;

        case 'depositFunds':
          history.push('/account/deposit');
          break;

        case 'withdrawFunds':
          history.push('/account/withdraw');
          break;

        case 'limits':
          history.push('/account/limits');
          break;

        default:
          break;
      }
    }
  };

  const openDrawer = async () => {
    if (openMenu !== '') {
      await setOpen(true);
      setMenu(openMenu);
    }
  };

  useEffect(() => {
    if (query.has('page')) {
      setOpen(true);
      setMenu(query.get('page'));
    }
  }, []);

  useEffect(() => {
    // Disable back page scrolling
    if (open){
      document.body.classList.add('disable-scroll');
    } else {
      document.body.classList.remove('disable-scroll');
    }
  }, [open]);

  useEffect(() => {
    openDrawer();
  }, [openMenu]);

  const handleWalletChange = (e) => {
    const { wallets } = customer;
    const wallet = _.find(wallets, { id: +e.target.value });

    dispatch.auth.selectLocalWallet(wallet);
  };

  const activeWalletsRadioGroup = () => {
    const { wallets } = customer;

    return (
      <RadioGroup
        className="user-drawer__user-menu__radio"
        aria-label="wallet"
        name="wallet"
        value={ localWalletSelected && localWalletSelected.id }
        onChange={ handleWalletChange }
      >
        {
          _.map(wallets, (wallet, index) => (
            <MenuItem key={ index } disableGutters={ true } className="user-drawer__user-menu__submenu">
              <FormControlLabel
                className="user-drawer__user-menu__submenu"
                value={ wallet.id }
                control={
                  <Radio
                    color="primary"
                    checkedIcon={ <Icon icon="radioButton" /> }
                    icon={ <Icon icon="radioButtonOpen" /> }
                  />
                }
                label={
                  <span className="user-drawer__user-menu__submenu">
                    { `${ _.capitalize(wallet?.type?.name) } ${ wallet.currency } ${ (wallet.balance / wallet.divisor).toFixed(2) }` }
                  </span>
                }
              />
            </MenuItem>
          ))
        }
      </RadioGroup>
    );
  };

  const handleOpen = e => {
    e.preventDefault();
    setOpen(!open);
  };

  const handleClose = e => {
    e.preventDefault();
    setMenu('settings');
    setOpen(false);
  };

  const handleBack = e => {
    e.preventDefault();
    if (menu === 'settings') {
      setOpen(false);
    } else {
      setMenu('settings');
    }
  };

  const mobileDrawer = () => {
    return (
      <ul className="user-drawer__menu">
        <li className="user-drawer__menu-item" onClick={ onUserMenuSelect('personalData') }>
          { t('account.personalData') }
        </li>
        <li className="user-drawer__menu-item" onClick={ onUserMenuSelect('bettingHistory') } >
          { t('account.bettingHistory') }
        </li>
        <li className="user-drawer__menu-item" onClick={ onUserMenuSelect('accountStatement') } >
          { t('account.accountStatement') }
        </li>
        <li className="user-drawer__menu-item" onClick={ onUserMenuSelect('depositFunds') } >
          { t('account.depositFunds') }
        </li>
        <li className="user-drawer__menu-item" onClick={ onUserMenuSelect('withdrawFunds') } >
          { t('account.withdrawFunds') }
        </li>
        <li className="user-drawer__menu-item" onClick={ onUserMenuSelect('limits') }>
          { t('accountLimits.selfLimitation') }
        </li>
        <li className="user-drawer__menu-item user-drawer__no-border">
          { t('account.activeWallet') }
        </li>
        <RadioGroup
          className="user-drawer__radio"
          aria-label="wallet"
          name="wallet"
          value={ localWalletSelected && localWalletSelected.type }
          onChange={ handleWalletChange }
        >
          {
            _.map(customer.wallets, (wallet, index) => (
              <li key={ index } className="user-drawer__submenu">
                <FormControlLabel
                  value={ wallet.type }
                  control={
                    <Radio
                      color="primary"
                      checkedIcon={ <Icon icon="radioButton" /> }
                      icon={ <Icon icon="radioButtonOpen" /> }
                    />
                  }
                />
                <div className="user-drawer__radio-label">
                  { `${ _.capitalize(wallet.type) }` }
                </div>
                <div>
                  { ` ${ wallet.currency } ${ (wallet.balance / wallet.divisor).toFixed(2) }` }
                </div>
              </li>
            ))
          }
          <li className="user-drawer__menu-item user-drawer__menu-item--last">
            <FormControlLabel
              control={
                <Checkbox
                  name="showBalance"
                  color="default"
                  checked={ settings.showBalance }
                  onChange={ handleSettings }
                  checkedIcon={ <Icon icon="check" /> }
                  icon={ <Icon icon="check" className="user-drawer__user-menu__icon" /> }
                />
              }
              label={ t('account.showActiveWalletBalance') }
            />
          </li>
        </RadioGroup>
        <li className="user-drawer__menu-item user-drawer__no-border" onClick={ onUserMenuSelect('logout') }>{ t('logout') }</li>
      </ul>
    );
  };

  if (isMobileOnly || (isTablet && isPortrait)){

    return (
      <>
        <div
          className={
            classNames({
              'user-drawer' : true,
              'user-drawer__btn__label-dark': open,
              'user-drawer__btn__label': !open
            })
          }
          onClick={ handleOpen }
        >
          <Icon icon="user" className="user-drawer__btn--icon" />
          {
            settings.showBalance && (
              <div className="user-drawer__balance-label">{
                `${ localWalletSelected && ((localWalletSelected?.balance / localWalletSelected?.divisor) || 0) }`
              }</div>
            )
          }
        </div>
        <Drawer
          className="user-drawer__drawer user-drawer--scroll"
          anchor="right"
          open={ open }
          onClose={ handleOpen }
        >
          <div
            className="container__scroll"
            ref={ drawer }
          >
            <div
              className={
                classNames({
                  'user-drawer__short': menu !== 'settings',
                  'user-drawer__back': menu === 'settings'
                })
              }
              onClick={ handleBack }
            >
              <div className="user-drawer__user-arrow">
                { menu !== 'settings' && <Icon icon="arrow" className="user-drawer__arrow" /> }
                {
                  menu === 'settings' &&
                    <>
                      <Icon icon="user" className="user-drawer__user-icon" />
                      <span>Hi, { customer.username }!</span>
                    </>
                }
                { menu === 'personalData' && <span>{ t('userDrawer.personalData') }</span> }
                { menu === 'accountHistory' && <span>{ t('userDrawer.history') }</span> }
                { menu === 'depositFunds' && <span>{ t('userDrawer.deposit') }</span> }
                { menu === 'withdrawFunds' && <span>{ t('userDrawer.withdraw') }</span> }
                { menu === 'limits' && <span>{ t('userDrawer.limits') }</span> }
              </div>
              <Icon icon="close" className="user-drawer__close" onClick={ handleClose } />
            </div>
            { menu === 'settings' && mobileDrawer() }
            { menu === 'personalData' && <PersonalData /> }
            { (menu === 'bettingHistory' || menu === 'accountStatement') && <AccountHistory refElement={ drawer } tabSelected={ menu } /> }
            { menu === 'depositFunds' && <DepositFunds /> }
            { menu === 'withdrawFunds' && <WithdrawFunds /> }
            { menu === 'limits' && <AccountLimits /> }
          </div>
        </Drawer>
      </>
    );
  } else {
    return (
      <>
        <DropDown
          value={ `Hi, ${ customer.username }!` }
          className={
            classNames({
              'user-drawer__user-options ': true,
              'dropdown--white': true,
              'dropdown--full': RETAIL_MODE
            })
          }
          IconComponent={
            <>
              <Icon icon="user" className="user-drawer__user-icon" />
              <Icon icon="arrow" />
            </>
          }
          popperClass="user-drawer__user-dropdown"
          iconMargin={ true }
          isRetail={ RETAIL_MODE }
        >
          <MenuList className="user-drawer__user-menu">
            {
              !RETAIL_MODE && <MenuItem className="user-drawer__user-menu__strong" onClick={ onUserMenuSelect('personalData') }>
                <strong>{ t('account.personalData') }</strong>
              </MenuItem>
            }
            {
              !RETAIL_MODE && <>
                <MenuItem className="user-drawer__user-menu__strong" onClick={ onUserMenuSelect('bettingHistory') }>
                  <strong>{ t('account.bettingHistory') }</strong>
                </MenuItem>
                <MenuItem className="user-drawer__user-menu__strong" onClick={ onUserMenuSelect('accountStatement') }>
                  <strong>{ t('account.accountStatement') }</strong>
                </MenuItem>

                <div className="user-drawer__user-menu__divider" />
                <MenuItem className="user-drawer__user-menu__strong" onClick={ onUserMenuSelect('depositFunds') }>
                  <strong>{ t('account.depositFunds') }</strong>
                </MenuItem><MenuItem className="user-drawer__user-menu__strong" onClick={ onUserMenuSelect('withdrawFunds') }>
                  <strong>{ t('account.withdrawFunds') }</strong>
                </MenuItem><div className="user-drawer__user-menu__divider"></div></>
            }
            <MenuItem className="user-drawer__user-menu__strong user-drawer__user-menu__no-cursor">
              <strong>{ t('account.activeWallet') }</strong>
            </MenuItem>
            { activeWalletsRadioGroup() }
            <MenuItem className="user-drawer__user-menu__submenu">
              <FormControlLabel
                control={
                  <Checkbox
                    name="showBalance"
                    color="default"
                    checked={ settings.showBalance }
                    onChange={ handleSettings }
                    checkedIcon={ <Icon icon="check" /> }
                    icon={ <Icon icon="check" className="user-drawer__user-menu__icon" /> }
                  />
                }
                label={ t('account.showActiveWalletBalance') }
              />
            </MenuItem>
            <div className="user-drawer__user-menu__divider"></div>
            <MenuItem className="user-drawer__user-menu__submenu" onClick={ onUserMenuSelect('logout') }>
              <strong>{ t('logout') }</strong>
            </MenuItem>
          </MenuList>
        </DropDown>
      </>
    );
  }
}

export default React.memo(UserDrawer);
